import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, MapPin, Heart, MessageCircle, ExternalLink, ChevronDown, ChevronUp, Briefcase, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Database } from '@/integrations/supabase/types';
import { useMembershipContext } from '@/components/layout/AppLayout';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface MemberCardProps {
  profile: Profile;
}

interface BioSection {
  label: string;
  value: string;
  isList: boolean;
}

const BIO_MARKERS = [
  { prefix: 'Current Title:', label: 'Title', isList: false },
  { prefix: 'Current Company:', label: 'Company', isList: false },
  { prefix: 'Primary Function:', label: 'Function', isList: false },
  { prefix: 'Contributing to QBN:', label: 'Contributing', isList: true },
  { prefix: 'Interested in through QBN:', label: 'Interested in', isList: true },
];

const TAG_COLORS = [
  'bg-pink-500/15 border-pink-500/30 text-pink-600 dark:text-pink-300',
  'bg-teal-500/15 border-teal-500/30 text-teal-600 dark:text-teal-300',
  'bg-violet-500/15 border-violet-500/30 text-violet-600 dark:text-violet-300',
  'bg-amber-500/15 border-amber-500/30 text-amber-600 dark:text-amber-300',
  'bg-sky-500/15 border-sky-500/30 text-sky-600 dark:text-sky-300',
];

function parseBioSections(bio: string | null): BioSection[] | null {
  if (!bio?.trim()) return null;

  const sections: BioSection[] = [];
  const paragraphs = bio.split('\n\n');

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;

    const matched = BIO_MARKERS.find((m) => trimmed.startsWith(m.prefix));
    if (matched) {
      const value = trimmed.slice(matched.prefix.length).trim();
      if (value) sections.push({ label: matched.label, value, isList: matched.isList });
    }
  }

  return sections.length > 0 ? sections : null;
}

export function MemberCard({ profile }: MemberCardProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const { isPending } = useMembershipContext();

  const bioSections = parseBioSections(profile.bio);
  const hasRawBio = !!profile.bio?.trim() && !bioSections;
  const hasExpandedContent = !!bioSections || hasRawBio || profile.role || profile.industry;

  return (
    <Card
      className="card-qbn transition-all cursor-pointer"
      onClick={() => hasExpandedContent && setExpanded(!expanded)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-14 w-14 flex-shrink-0">
            <SignedAvatarImage src={profile.avatar_url || undefined} />
            <AvatarFallback className="bg-gradient-primary text-white font-medium text-lg">
              {profile.full_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">{profile.full_name}</h3>
                  {profile.is_mentor && (
                    <Badge className="badge-mentor text-xs">
                      <Heart className="h-3 w-3 mr-1" />
                      Matchups
                    </Badge>
                  )}
                </div>
                {profile.pronouns && (
                  <span className="text-xs text-muted-foreground">({profile.pronouns})</span>
                )}
              </div>
              {hasExpandedContent && (
                expanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                )
              )}
            </div>

            {profile.headline && (
              <p className="text-sm text-muted-foreground mt-1">{profile.headline}</p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              {profile.company && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {profile.company}
                </span>
              )}
              {profile.city_cohort && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {profile.city_cohort}
                </span>
              )}
            </div>

            {/* Expanded section */}
            {expanded && (
              <div className="mt-3 pt-3 border-t border-border space-y-3">
                {profile.role && (
                  <div className="flex items-start gap-2 text-sm">
                    <Briefcase className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{profile.role}</span>
                  </div>
                )}
                {profile.industry && (
                  <div className="flex items-start gap-2 text-sm">
                    <Globe className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{profile.industry}</span>
                  </div>
                )}

                {bioSections?.map((section) => (
                  <div key={section.label} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {section.label}
                    </p>
                    {section.isList ? (
                      <div className="flex flex-wrap gap-1.5">
                        {section.value.split(',').map((item, i) => {
                          const trimmed = item.trim();
                          if (!trimmed) return null;
                          return (
                            <span
                              key={trimmed}
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium border ${TAG_COLORS[i % TAG_COLORS.length]}`}
                            >
                              {trimmed}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground">{section.value}</p>
                    )}
                  </div>
                ))}

                {hasRawBio && (
                  <p className="text-sm text-foreground whitespace-pre-wrap">{profile.bio}</p>
                )}
              </div>
            )}

            {!isPending && (
              <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-primary border-primary/20 hover:bg-primary/10"
                  onClick={() => navigate(`/messages?to=${profile.user_id}`)}
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  Message
                </Button>
                {profile.linkedin_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                    asChild
                  >
                    <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      LinkedIn
                    </a>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
